import type { AppointmentTransferStatus } from '@mend/types/appointment';

import * as React from 'react';
import { Autocomplete, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { AppointmentTypesAutocomplete } from '#components/Autocomplete/AppointmentTypesAutocomplete.tsx';
import { appointmentStatuses, appointmentTypes } from '#lib/react-query/queries.ts';
import { useFilters } from '../Filters/FiltersContext.tsx';
import { useFilterAutocomplete } from '../Filters/use-filter-autocomplete.ts';

export function TransferQueueAppointmentTypeFilter(props: { defaultValue: number[] }) {
  const query = useQuery(appointmentTypes());
  const filter = useFilterAutocomplete({
    searchParamKey: 'atId',
    defaultValue: props.defaultValue,
    data: query.data,
  });

  return (
    <AppointmentTypesAutocomplete
      {...filter.getAutocompleteProps()}
      inputLabel="Appointment Type"
    />
  );
}

export function TransferQueueStatusFilter(props: { defaultValue: number[] }) {
  const query = useQuery(appointmentStatuses);
  const filters = useFilters();
  const [selectedStatuses, setSelectedStatuses] = React.useState<AppointmentTransferStatus[]>([]);
  const [hasInitialized, setHasInitialized] = React.useState(props.defaultValue.length === 0);

  React.useEffect(() => {
    if (hasInitialized || !query.data) return;
    setSelectedStatuses(query.data.filter((status) => props.defaultValue.includes(status.id)));
    setHasInitialized(true);
  }, [hasInitialized, props.defaultValue, query.data]);

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      limitTags={1}
      loading={query.isLoading}
      options={query.data ?? []}
      value={selectedStatuses}
      getOptionLabel={(status) => String(status.name)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_event, statuses) => {
        setSelectedStatuses(statuses);
        filters.onChange({ appointmentStatusIds: statuses.map((status) => String(status.id)) });
      }}
      renderInput={(params) => <TextField {...params} label="Queue Entry Status" />}
    />
  );
}

export function TransferQueueAppointmentIdFilter(props: { defaultValue: number | undefined }) {
  const filters = useFilters();

  function applyValue(rawValue: string) {
    const appointmentId = Number(rawValue);
    filters.onChange({
      appointmentId:
        Number.isInteger(appointmentId) && appointmentId > 0 ? appointmentId : undefined,
    });
  }

  return (
    <TextField
      defaultValue={props.defaultValue ?? ''}
      label="Appointment ID"
      inputMode="numeric"
      onBlur={(event) => applyValue(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
    />
  );
}

export function TransferQueueStateFilter(props: { defaultValue: 'all' | 'current' | 'completed' }) {
  const filters = useFilters();

  return (
    <FormControl fullWidth>
      <InputLabel id="transfer-queue-state-label">Queue State</InputLabel>
      <Select
        labelId="transfer-queue-state-label"
        label="Queue State"
        defaultValue={props.defaultValue}
        onChange={(event) => filters.onChange({ queueState: event.target.value })}
      >
        <MenuItem value="all">All episodes</MenuItem>
        <MenuItem value="current">Currently queued</MenuItem>
        <MenuItem value="completed">Completed</MenuItem>
      </Select>
    </FormControl>
  );
}

export function TransferQueueDisconnectionFilter(props: { defaultValue: 'all' | 'yes' | 'no' }) {
  const filters = useFilters();

  return (
    <FormControl fullWidth>
      <InputLabel id="transfer-queue-disconnection-label">Patient Disconnected</InputLabel>
      <Select
        labelId="transfer-queue-disconnection-label"
        label="Patient Disconnected"
        defaultValue={props.defaultValue}
        onChange={(event) => filters.onChange({ patientDisconnected: event.target.value })}
      >
        <MenuItem value="all">All episodes</MenuItem>
        <MenuItem value="yes">Disconnected</MenuItem>
        <MenuItem value="no">Did not disconnect</MenuItem>
      </Select>
    </FormControl>
  );
}
